"use client";
import { Button } from "@/components/ui/button";
import { SignInDialogWithTrigger } from "@/components/auth/sign-in-dialog";
import { signOut, useSession } from "@/lib/auth-client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import * as Sentry from "@sentry/browser";
import { toast } from "sonner";
import { User } from "better-auth";
import { UserAvatar } from "@/components/primitives/user-avatar";
import { Link } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { useIsClient } from "@/lib/hooks/useIsClient";
import { FeedbackDialog } from "@/components/feedback-dialog";
import {
  BookOpen,
  CircleUserRound,
  Settings,
  Megaphone,
  LogOut,
  Package,
  Bug,
} from "lucide-react";
function LoggedOut() {
  return (
    <SignInDialogWithTrigger analyticsFrom="navigation">
      <Button variant="outline">Sign in</Button>
    </SignInDialogWithTrigger>
  );
}
function LoggedIn({ user }: { user: User }) {
  const router = useRouter();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            className="relative h-10 w-10 rounded-full p-2"
          />
        }
      >
        <UserAvatar userId={user.id} name={user.name} size="md" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-48" align="end">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="max-w-64">
            <div className="text-foreground truncate font-medium">
              {user.name}
            </div>
            <div className="truncate text-xs font-normal">{user.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuItem
            render={
              <Link
                to="/users/$userId"
                params={{ userId: user.id }}
                className="w-full"
              />
            }
          >
            <CircleUserRound />
            My public profile
          </DropdownMenuItem>
          <DropdownMenuItem render={<Link to="/recipes" className="w-full" />}>
            <BookOpen />
            My recipes
          </DropdownMenuItem>
          <DropdownMenuItem
            render={
              <Link
                to="/ingredients"
                search={{ view: "mine" }}
                className="w-full"
              />
            }
          >
            <Package />
            My ingredients
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem render={<Link to="/account" className="w-full" />}>
          <Settings />
          Account settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem closeOnClick={false}>
          <FeedbackDialog>
            <button className="flex w-full items-center gap-3 text-left">
              <Megaphone className="h-4 w-4 shrink-0" />
              Leave feedback
            </button>
          </FeedbackDialog>
        </DropdownMenuItem>
        <DropdownMenuItem closeOnClick={false}>
          <FeedbackDialog>
            <button className="flex w-full items-center gap-3 text-left">
              <Bug className="h-4 w-4 shrink-0" />
              Report a bug
            </button>
          </FeedbackDialog>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            try {
              await signOut();
              toast.success("Signed out");
              router.invalidate();
            } catch (error) {
              Sentry.captureException(error);
              toast.error("Failed to sign out");
            }
          }}
        >
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
export function Account() {
  const isClient = useIsClient();
  const clientSession = useSession();
  if (!isClient || clientSession.isPending) {
    return null;
  }
  if (!clientSession.data) {
    return <LoggedOut />;
  }
  return <LoggedIn user={clientSession.data.user} />;
}
